import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Eye, Bookmark, Star, PenLine } from "lucide-react";
import { Avatar } from "@/components/Avatar";
import { Modal } from "@/components/FindPeople";
import { PostCard, PostComposer, PostImages } from "@/components/Posts";
import { ListCard } from "@/pages/ListsTab";
import { Comments, when } from "@/components/ContentBits";
import { ReactionBar, ratingLabel, EntryExtras } from "@/components/DiaryPoster";

function Person({ who, children }) {
  return (
    <div className="flex items-center gap-3">
      <Link to={`/u/${who.username}`} className="flex-shrink-0" aria-label={`${who.username}'s profile`}><Avatar avatar={who.avatar} url={who.avatar_url} name={who.username || "?"} size="sm" /></Link>
      <div className="min-w-0 leading-tight">
        <Link to={`/u/${who.username}`} className="font-semibold text-[hsl(var(--gold))] hover:underline" data-testid="community-person">{who.username}</Link>
        <p className="text-xs text-muted-foreground">{children}</p>
      </div>
    </div>
  );
}

function Stars({ n }) {
  return n ? <span className="text-[hsl(var(--gold))]" aria-label={`${n} out of 5`}>{"★".repeat(n)}<span className="text-muted-foreground/40">{"★".repeat(5 - n)}</span></span> : null;
}

/** A shared diary entry on the show page: who, when, stars and (if they shared it) their thoughts. Friends can react and comment. */
function EntryItem({ e, onChange }) {
  const friend = e.level === "friend";
  const thoughts = e.review || e.verdict || e.note;
  return (
    <article className="rounded-2xl border border-border bg-card p-4" data-testid="community-entry">
      <Person who={e}>saw it on {when(e.date)}</Person>
      <div className="mt-2 flex flex-wrap items-center gap-x-3 text-sm">
        <Stars n={e.rating} />{e.rating ? <span className="text-xs text-muted-foreground">{ratingLabel(e.rating)}</span> : null}
      </div>
      {thoughts && <p className="mt-2 whitespace-pre-wrap break-words text-base leading-relaxed text-foreground/90" data-testid="community-entry-text">{thoughts}</p>}
      {friend && <EntryExtras e={e} />}
      {friend && <PostImages images={e.images} who={e.username} what="diary entry" />}
      {friend && <ReactionBar entry={e} onChange={onChange} />}
      {friend && <Comments kind="entry" id={e.id} count={e.comment_count || 0} onCount={(c) => c !== (e.comment_count || 0) && onChange({ ...e, comment_count: c })} />}
    </article>
  );
}

/** "What people are saying" on a show page. */
export default function ShowCommunity({ show, user, onSignIn }) {
  const [limit, setLimit] = useState(10);
  const [data, setData] = useState(null);
  const [writing, setWriting] = useState(false);

  const load = useCallback(() => {
    api.get(`/shows/${show.id}/community?limit=${limit}`).then((r) => setData(r.data)).catch(() => setData((d) => d || false));
  }, [show.id, limit]);
  useEffect(() => { load(); }, [load]);

  if (data === false) return null;                       // never let this section break the show page
  if (!data) return null;
  const { stats, friends_seen: fs = [], posts = [], entries = [], lists = [] } = data;
  const patch = (key) => (u) => setData((d) => ({ ...d, [key]: d[key].map((x) => (x.id === u.id ? { ...x, ...u } : x)) }));
  const empty = !posts.length && !entries.length && !lists.length;
  const more = data.has_more_posts || data.has_more_entries || data.has_more_lists;
  const startPost = () => (user ? setWriting(true) : onSignIn());

  return (
    <section data-testid="community">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-3 font-serif text-2xl font-semibold text-foreground">
          <span className="h-6 w-1 rounded-full bg-[#C89B3C]" /> What people are saying
        </h2>
        <button type="button" onClick={startPost} data-testid="community-post" className="inline-flex items-center gap-2 rounded-full bg-[#9E1B28] px-5 py-2.5 text-sm font-semibold text-amber-50 hover:bg-[#B3202F]">
          <PenLine className="h-4 w-4" /> Post about this show
        </button>
      </div>

      {(stats.seen > 0 || stats.want > 0 || stats.rating_count > 0) && (
        <div className="mt-4 flex flex-wrap gap-2 text-sm" data-testid="community-stats">
          {stats.seen > 0 && <span className="inline-flex items-center gap-1.5 rounded-full border border-border px-3.5 py-1.5"><Eye className="h-4 w-4 text-[#C89B3C]" /> {stats.seen} {stats.seen === 1 ? "member has" : "members have"} seen this</span>}
          {stats.want > 0 && <span className="inline-flex items-center gap-1.5 rounded-full border border-border px-3.5 py-1.5"><Bookmark className="h-4 w-4 text-[#C89B3C]" /> {stats.want} want to see it</span>}
          {stats.rating_count > 0 && <span className="inline-flex items-center gap-1.5 rounded-full border border-border px-3.5 py-1.5" data-testid="community-rating"><Star className="h-4 w-4 fill-[#E6C071] text-[hsl(var(--gold))]" /> {stats.rating_avg} from {stats.rating_count} {stats.rating_count === 1 ? "rating" : "ratings"}</span>}
        </div>
      )}

      {fs.length > 0 && (
        <div className="mt-4 rounded-2xl border border-[#C89B3C]/30 bg-[#C89B3C]/10 p-4" data-testid="community-friends">
          <p className="text-sm font-semibold text-foreground">{data.friends_seen_total === 1 ? "1 of your friends has" : `${data.friends_seen_total} of your friends have`} seen this</p>
          <div className="mt-3 flex flex-wrap gap-4">
            {fs.map((f) => <Person key={f.username} who={f}>{f.rating ? <Stars n={f.rating} /> : "Seen it"}</Person>)}
          </div>
        </div>
      )}

      {!data.signed_in && (
        <div className="mt-4 rounded-2xl border border-dashed border-[#C89B3C]/50 bg-card p-4 text-sm" data-testid="community-join">
          <p>Sign in to see what your friends thought, and to share your own.</p>
          <button type="button" onClick={onSignIn} className="mt-2 rounded-full border border-[#C89B3C]/60 px-4 py-1.5 font-semibold text-[hsl(var(--gold))]">Sign in or join</button>
        </div>
      )}

      {empty && (
        <p className="mt-4 rounded-xl border border-dashed border-border bg-card p-6 text-center text-muted-foreground" data-testid="community-empty">
          Nobody has shared a thought about {show.name} yet. Be the first.
        </p>
      )}

      {posts.length > 0 && (
        <div className="mt-6 space-y-4" data-testid="community-posts">
          {posts.map((p) => <PostCard key={p.id} post={p} mine={!!p.mine_post} canReact={!p.mine_post && !!p.by_friend} canReport={!p.mine_post && !!user} canComment={!!p.mine_post || !!p.by_friend} onChange={patch("posts")} />)}
        </div>
      )}
      {entries.length > 0 && (
        <div className="mt-6 space-y-4" data-testid="community-entries">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">From members' diaries</h3>
          {entries.map((e) => <EntryItem key={e.id} e={e} onChange={patch("entries")} />)}
        </div>
      )}
      {lists.length > 0 && (
        <div className="mt-6 space-y-4" data-testid="community-lists">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Lists featuring {show.name}</h3>
          {lists.map((l) => <ListCard key={l.id} list={l} canReact={!!l.by_friend} canReport canComment={!!l.by_friend} onChange={patch("lists")} />)}
        </div>
      )}
      {more && limit < 30 && (
        <button type="button" onClick={() => setLimit(30)} data-testid="community-more" className="mt-5 rounded-full border border-border px-5 py-2 text-sm font-medium hover:bg-secondary">Show more</button>
      )}

      {writing && (
        <Modal title="New post" onClose={() => setWriting(false)} testid="community-compose" wide backdropCloses={false}>
          <div className="mt-3">
            <PostComposer presetShow={{ id: show.id, name: show.name }} defaultVisibility="public" onPosted={() => { setWriting(false); load(); }} onCancel={() => setWriting(false)} />
          </div>
        </Modal>
      )}
    </section>
  );
}